import {
  DriverPreference,
  ShipmentPackagingType,
  ShipmentPalletType,
  ShipmentWeightUnit,
} from "app/pages/shipments/types/domain";
import { CargoGroupType } from "features/cargo-groups/api/types";
import i18next from "i18next";
import { TrailerSizeEnum } from "types/TrailerSize";
import { TrailerTypeEnum } from "types/TrailerType";
import { z } from "zod";

const addressSchema = z.object({
  id: z.number().optional(),
  name: z.string().nullable().optional(),
  address_1: z.string().nullable().optional(),
  address_2: z.string().nullable().optional(),
  city: z.string().nullable().optional(),
  zip: z.string().nullable().optional(),
  country_code: z.string().nullable().optional(),
  country_name: z.string().nullable().optional(),
  province_code: z.string().nullable().optional(),
  province_name: z.string().nullable().optional(),
  google_place_id: z.string().nullable().optional(),
  google_place_description: z.string().nullable().optional(),
  type: z.string().nullable().optional(),
});

const stopSchema = z.object({
  address: addressSchema.refine(
    (val) => {
      return val != undefined && val != null && (val.city || val.address_1 || val.google_place_description);
    },
    {
      message: i18next.t("quoteRequest:LocationStep.addressRequired"),
    }
  ),
  start: z.string().optional().nullable(),
  end: z.string().optional().nullable(),
  note: z.string().optional().nullable(),
  isTimeTbd: z.boolean().optional().nullable(),
});

export const locationSchema = z.object({
  stops: stopSchema.array().min(2, { message: "Quote request must have at least two stops" }),
});

export type StopSchema = z.infer<typeof stopSchema>;
export type LocationSchema = z.infer<typeof locationSchema>;

export const baseCargoGroup = z.object({
  packaging_type: z.nativeEnum(ShipmentPackagingType),
  cargo_group_type: z.nativeEnum(CargoGroupType),
  count: z.number().optional(),
  commodities: z.string().optional(),
  weight_per_count: z.number().optional(),
  height_per_count: z.number().optional(),
  length_per_count: z.number().optional(),
  width_per_count: z.number().optional(),
  volume_per_count: z.number().optional(),
  is_stackable: z.boolean().optional(),
  equipment_type: z.nativeEnum(TrailerTypeEnum).optional(),
  equipment_size: z.nativeEnum(TrailerSizeEnum).optional(),
  min_temp: z.number().optional().nullable(),
  max_temp: z.number().optional().nullable(),
  requires_genset: z.boolean().optional(),
});

type CreateCargoGroup = z.infer<typeof baseCargoGroup> & {
  handling_units?: (number | string)[];
  packing_units?: (number | string)[];
  // handling_units?: CreateCargoGroup[];
  // packing_units?: CreateCargoGroup[];
  cargo_unit_id?: number;
  handling_unit_id?: number;
};

const createCargoGroup: z.ZodType<CreateCargoGroup> = baseCargoGroup.extend({
  // handling_units: z.lazy(() => createCargoGroup.array()).optional(),
  // packing_units: z.lazy(() => createCargoGroup.array()).optional(),
  handling_units: z.union([z.number(), z.string()]).array().optional(),
  packing_units: z.union([z.number(), z.string()]).array().optional(),
  cargo_unit_id: z.number().optional(),
  handling_unit_id: z.number().optional(),
});

const baseLoadAttributesSchema = locationSchema.and(
  z.object({
    deadline_respond_at: z.string().optional(),
    reference_number: z.string().optional(),
    internal_reference_number: z.string().optional().nullable(),
    hide_internal_ref_till_dispatch: z.boolean().optional(),
    truck_quantity: z.number(),
    driver_preference: z.nativeEnum(DriverPreference),
    trailer_type: z.nativeEnum(TrailerTypeEnum),
    trailer_size: z.nativeEnum(TrailerSizeEnum),
    packaging_type: z.nativeEnum(ShipmentPackagingType).optional(),
    packing_count: z.number().optional(),
    unit_count: z.number().optional(),
    pallet_count: z.number().optional(),
    total_weight: z.number().optional(),
    weight_per_unit: z.number().optional(),
    weight_unit: z.nativeEnum(ShipmentWeightUnit).optional(),
    commodities: z.string(),
    is_hazardous: z.boolean(),
    hazardous_notes: z.string().optional(),
    goods_value: z.number().optional(),
    notes: z.string().optional(),
    min_temp: z.number().optional(),
    max_temp: z.number().optional(),
  })
);

// Schema for when cargo groups feature is DISABLED (original behavior)
export const loadAttributesSchema = baseLoadAttributesSchema.and(
  z.discriminatedUnion("is_palletized", [
    z.object({
      is_palletized: z.literal(true),
      pallet_type: z.nativeEnum(ShipmentPalletType),
    }),
    z.object({
      is_palletized: z.literal(false),
      pallet_type: z.nativeEnum(ShipmentPalletType).optional(),
    }),
  ])
);

export const partnersSchema = loadAttributesSchema.and(
  z.object({
    partners: z.object({ contact_id: z.number() }).array().min(1, { message: "At least one partner must be selected" }),
  })
);

export type PartnersScheam = z.infer<typeof partnersSchema>;

export const teamSchema = partnersSchema.and(
  z.object({
    teammates: z.object({ contact_id: z.number() }).array().optional(),
  })
);
export const loadAttributesSchemaWithCargoGroups = baseLoadAttributesSchema.and(
  z.object({
    is_palletized: z.boolean().optional(),
    pallet_type: z.nativeEnum(ShipmentPalletType).optional(),
    cargo_groups: z.array(createCargoGroup).min(1),
  })
);
export const partnersSchemaWithCargoGroups = loadAttributesSchemaWithCargoGroups.and(
  z.object({
    partners: z.object({ contact_id: z.number() }).array().min(1, { message: "At least one partner must be selected" }),
  })
);

export const teamSchemaWithCargoGroups = partnersSchemaWithCargoGroups.and(
  z.object({
    teammates: z.object({ contact_id: z.number() }).array().optional(),
  })
);

// eslint-disable-next-line @typescript-eslint/explicit-module-boundary-types
export const getLoadAttributesSchema = (enableFTLCargoGroups: boolean) => {
  return enableFTLCargoGroups ? loadAttributesSchemaWithCargoGroups : loadAttributesSchema;
};

// Factory function to get the right team and partner schema based on feature flag
// eslint-disable-next-line @typescript-eslint/explicit-module-boundary-types
export const getTeamSchema = (enableFTLCargoGroups: boolean) => {
  return enableFTLCargoGroups ? teamSchemaWithCargoGroups : teamSchema;
};

// eslint-disable-next-line @typescript-eslint/explicit-module-boundary-types
export const getPartnersSchema = (enableFTLCargoGroups: boolean) => {
  return enableFTLCargoGroups ? partnersSchemaWithCargoGroups : partnersSchema;
};

export type TeamSchema = z.infer<typeof teamSchema>;

export type FinalSchema = TeamSchema;
